<template>
  <div class="device-license-detail">
    <div class="page-header">
      <a-button icon="arrow-left" class="back-btn" @click="backToList">
        返回列表
      </a-button>
    </div>

    <a-spin :spinning="loading">
      <a-row :gutter="16" class="stat-row">
        <a-col :span="6">
          <div class="stat-card">
            <div class="stat-label">License配额</div>
            <div class="stat-value">{{ detail.quota }} 台</div>
            <div class="stat-extra">配置上限</div>
          </div>
        </a-col>
        <a-col :span="6">
          <div class="stat-card">
            <div class="stat-label">已使用设备</div>
            <div class="stat-value" style="color: #1890ff">{{ detail.used }} 台</div>
            <!-- 备注：接口 used 为「已使用设备数」，原型文案为「当前在线设备数」，语义不完全一致 -->
            <div class="stat-extra">已使用设备数</div>
          </div>
        </a-col>
        <a-col :span="6">
          <div class="stat-card">
            <div class="stat-label">剩余可用</div>
            <div class="stat-value" style="color: #52c41a">{{ detail.remain }} 台</div>
            <div class="stat-extra">可继续添加设备</div>
          </div>
        </a-col>
        <a-col :span="6">
          <div class="stat-card">
            <div class="stat-label">使用率</div>
            <div class="stat-value" style="color: #fa8c16">{{ usageRate }}%</div>
            <a-progress
              :percent="Number(usageRate)"
              :show-info="false"
              status="active"
              stroke-color="#fa8c16"
            />
          </div>
        </a-col>
      </a-row>

      <div class="form-card">
        <div class="form-card-title">License配置信息</div>
        <!-- 查看模式：纯文本 -->
        <div class="form-card-body" v-if="isView">
          <div class="field-grid">
            <div class="view-item">
              <div class="view-label">租户名称</div>
              <div class="view-value tenant-name">
                <a-avatar
                  :size="24"
                  :style="{ backgroundColor: detail.avatarColor, marginRight: '8px' }"
                >
                  {{ detail.avatarText }}
                </a-avatar>
                {{ detail.tenantName || '--' }}
              </div>
            </div>
            <div class="view-item">
              <div class="view-label">租户ID</div>
              <!-- 备注：接口仅有 tenantId，无独立 tenantCode 字段 -->
              <div class="view-value">{{ detail.tenantId || '--' }}</div>
            </div>
            <div class="view-item">
              <div class="view-label">License配额数量</div>
              <div class="view-value">{{ detail.quota }} 台</div>
            </div>
            <div class="view-item">
              <div class="view-label">预警阈值</div>
              <div class="view-value">{{ detail.warningThreshold }}%</div>
            </div>
            <div class="view-item">
              <div class="view-label">生效起始日期</div>
              <div class="view-value">{{ formatDate(detail.startDate) }}</div>
            </div>
            <div class="view-item">
              <div class="view-label">生效结束日期</div>
              <div class="view-value">{{ formatDate(detail.endDate) }}</div>
            </div>
            <div class="view-item field-full">
              <div class="view-label">备注说明</div>
              <div class="view-value">{{ detail.remark || '--' }}</div>
            </div>
          </div>
        </div>

        <!-- 编辑模式：表单控件 -->
        <div class="form-card-body" v-else>
          <a-form :form="form" layout="vertical">
            <div class="field-grid">
              <a-form-item label="租户名称">
                <a-input :value="detail.tenantName" disabled>
                  <a-avatar
                    slot="prefix"
                    :size="20"
                    :style="{
                      backgroundColor: detail.avatarColor,
                      marginRight: '4px',
                    }"
                  >
                    {{ detail.avatarText }}
                  </a-avatar>
                </a-input>
              </a-form-item>
              <a-form-item label="租户ID">
                <a-input :value="detail.tenantId" disabled />
              </a-form-item>
              <a-form-item label="License配额数量">
                <a-input
                  suffix="台"
                  v-decorator="[
                    'quota',
                    {
                      initialValue: detail.quota != null ? String(detail.quota) : '',
                      rules: [
                        { required: true, message: '请输入License配额数量' },
                        {
                          pattern: /^[1-9]\d*$/,
                          message: '请输入正整数',
                        },
                      ],
                    },
                  ]"
                />
              </a-form-item>
              <a-form-item label="预警阈值">
                <a-input
                  suffix="%"
                  v-decorator="[
                    'warningThreshold',
                    {
                      initialValue:
                        detail.warningThreshold != null
                          ? String(detail.warningThreshold)
                          : '10',
                      rules: [
                        { required: true, message: '请输入预警阈值' },
                        {
                          pattern: /^(100|[1-9]?\d)$/,
                          message: '请输入 0-100 的整数',
                        },
                      ],
                    },
                  ]"
                />
              </a-form-item>
              <a-form-item label="生效起始日期">
                <a-date-picker
                  style="width: 100%"
                  v-decorator="[
                    'startDate',
                    {
                      initialValue: detail.startDate
                        ? $moment(detail.startDate)
                        : null,
                      rules: [{ required: true, message: '请选择生效起始日期' }],
                    },
                  ]"
                />
              </a-form-item>
              <a-form-item label="生效结束日期">
                <a-date-picker
                  style="width: 100%"
                  v-decorator="[
                    'endDate',
                    {
                      initialValue: detail.endDate
                        ? $moment(detail.endDate)
                        : null,
                      rules: [{ required: true, message: '请选择生效结束日期' }],
                    },
                  ]"
                />
              </a-form-item>
              <a-form-item label="备注说明" class="field-full">
                <div class="textarea-count-wrap">
                  <a-textarea
                    :rows="3"
                    :maxLength="200"
                    v-decorator="[
                      'remark',
                      {
                        initialValue: detail.remark || '',
                      },
                    ]"
                    @change="onRemarkChange"
                  />
                  <span class="textarea-count">{{ remarkLen }}/200</span>
                </div>
              </a-form-item>
            </div>
          </a-form>
        </div>
        <div class="form-actions" v-if="isView">
          <a-button type="primary" style="margin-left: 8px" @click="switchToEdit">
            编辑
          </a-button>
        </div>
        <div class="form-actions" v-else>
          <a-button @click="cancelEdit">取消</a-button>
          <a-button
            type="primary"
            icon="check"
            style="margin-left: 8px"
            :loading="saving"
            @click="handleSave"
          >
            保存修改
          </a-button>
        </div>
      </div>
    </a-spin>
  </div>
</template>

<script>
const AVATAR_COLORS = [
  '#1890ff',
  '#52c41a',
  '#722ed1',
  '#fa8c16',
  '#13c2c2',
  '#eb2f96',
  '#2f54eb',
]

const emptyDetail = {
  id: '',
  tenantName: '',
  tenantId: '',
  avatarText: '?',
  avatarColor: '#1890ff',
  quota: 0,
  used: 0,
  remain: 0,
  usedRate: 0,
  warningThreshold: 10,
  startDate: '',
  endDate: '',
  remark: '',
  status: '',
}

export default {
  name: 'DeviceLicenseDetail',
  data() {
    return {
      form: this.$form.createForm(this),
      mode: 'view',
      detail: { ...emptyDetail },
      loading: false,
      saving: false,
      remarkLen: 0,
    }
  },
  computed: {
    isView() {
      return this.mode === 'view'
    },
    usageRate() {
      if (this.detail.usedRate != null && this.detail.usedRate !== '') {
        return this.detail.usedRate
      }
      return '0.0'
      // if (!this.detail.quota) return '0.0'
      // return ((this.detail.used / this.detail.quota) * 100).toFixed(1)
    },
  },
  created() {
    this.initPage()
  },
  watch: {
    '$route.query': {
      handler() {
        this.initPage()
      },
      deep: true,
    },
  },
  methods: {
    onRemarkChange(e) {
      this.remarkLen = (e.target.value || '').length
    },
    formatDate(val) {
      if (!val) return '--'
      return this.$moment(val).format('YYYY-MM-DD')
    },
    getAvatarMeta(name, id) {
      const text = name ? String(name).charAt(0) : '?'
      const seed = String(id || name || '')
      let hash = 0
      for (let i = 0; i < seed.length; i++) {
        hash = (hash + seed.charCodeAt(i)) % AVATAR_COLORS.length
      }
      return {
        avatarText: text,
        avatarColor: AVATAR_COLORS[hash],
      }
    },
    normalizeDetail(data) {
      const avatar = this.getAvatarMeta(data.tenantName, data.tenantId)
      return {
        ...emptyDetail,
        ...data,
        id: String(data.id || ''),
        tenantId: String(data.tenantId || ''),
        ...avatar,
      }
    },
    async initPage() {
      const { id, mode } = this.$route.query
      this.mode = mode === 'edit' ? 'edit' : 'view'
      if (!id) {
        this.$message.warning('缺少 License id')
        this.backToList()
        return
      }
      await this.fetchDetail(id)
    },
    async fetchDetail(id) {
      this.loading = true
      try {
        const res = await this.$api.getDeviceLicenseDetail(id)
        if (res.code !== 0) {
          this.$message.error(res.message || '获取详情失败')
          return
        }
        this.detail = this.normalizeDetail(res.data || {})
        if (this.mode === 'edit') {
          this.remarkLen = (this.detail.remark || '').length
          this.$nextTick(() => {
            this.form.setFieldsValue({
              quota: this.detail.quota != null ? String(this.detail.quota) : '',
              warningThreshold:
                this.detail.warningThreshold != null
                  ? String(this.detail.warningThreshold)
                  : '10',
              startDate: this.detail.startDate
                ? this.$moment(this.detail.startDate)
                : null,
              endDate: this.detail.endDate
                ? this.$moment(this.detail.endDate)
                : null,
              remark: this.detail.remark || '',
            })
          })
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    backToList() {
      this.$router.push({ name: 'DeviceLicense' })
    },
    switchToEdit() {
      this.$router.replace({
        name: 'DeviceLicenseDetail',
        query: { id: this.detail.id, mode: 'edit' },
      })
    },
    cancelEdit() {
      this.$router.replace({
        name: 'DeviceLicenseDetail',
        query: { id: this.detail.id, mode: 'view' },
      })
    },
    handleSave() {
      this.form.validateFields(async (err, values) => {
        if (err) return
        const payload = {
          id: this.detail.id,
          tenantId: this.detail.tenantId,
          quota: Number(values.quota),
          warningThreshold: Number(values.warningThreshold),
          startDate: values.startDate
            ? values.startDate.format('YYYY-MM-DD HH:mm:ss')
            : undefined,
          endDate: values.endDate
            ? values.endDate.format('YYYY-MM-DD HH:mm:ss')
            : undefined,
          remark: values.remark || '',
        }
        this.saving = true
        try {
          const res = await this.$api.updateDeviceLicense(payload)
          if (res.code == 0) {
            this.$message.success('保存成功')
            this.$router.replace({
              name: 'DeviceLicenseDetail',
              query: { id: this.detail.id, mode: 'view' },
            })
            await this.fetchDetail(this.detail.id)
          } else {
            this.$message.error(res.message || '保存失败')
          }
        } catch (e) {
          console.error(e)
        } finally {
          this.saving = false
        }
      })
    },
  },
}
</script>

<style lang="less" scoped>
.device-license-detail {
  padding: 12px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.page-header {
  margin-bottom: 12px;
}

.stat-row {
  margin-bottom: 16px;
}

.stat-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 16px 20px;
}

.stat-label {
  font-size: 12px;
  color: #8c8c8c;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  line-height: 1;
  margin-bottom: 8px;
  color: #151515;
}

.stat-extra {
  font-size: 12px;
  color: #8c8c8c;
  line-height: 18px;
}

.view-item {
  margin-bottom: 20px;
}

.view-label {
  font-size: 13px;
  color: #8c8c8c;
  margin-bottom: 8px;
}

.view-value {
  font-size: 14px;
  color: #151515;
  line-height: 24px;
  min-height: 24px;
}

.tenant-name {
  display: flex;
  align-items: center;
}

.field-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 24px;
}

.field-full {
  grid-column: 1 / -1;
}

.form-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 12px;
  flex: 1;
  height: 0;
  display: flex;
  flex-direction: column;
}

.form-card-title {
  font-size: 16px;
  font-weight: 600;
  color: #151515;
  margin-bottom: 16px;
  line-height: 24px;
  padding: 0 12px;
}

.form-card-body {
  flex: 1;
  height: 0;
  overflow-y: auto;
  padding: 0 12px;
}

.form-actions {
  margin-top: 8px;
  text-align: right;
  padding-top: 8px;
  padding: 0 12px;
}

.textarea-count-wrap {
  position: relative;
}

.textarea-count-wrap /deep/ .ant-input {
  padding-bottom: 24px;
}

.textarea-count {
  position: absolute;
  right: 12px;
  bottom: 8px;
  font-size: 12px;
  line-height: 1;
  color: rgba(0, 0, 0, 0.45);
  pointer-events: none;
}

/deep/ .ant-spin-nested-loading,
/deep/ .ant-spin-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}
</style>
